import { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, RefreshCw, Phone, Mail } from "lucide-react";
import { useSEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lpl_admin_token";

const inputCls =
  "w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#1E88E5] focus:outline-none focus:ring-2 focus:ring-[#1E88E5]/30";

const AdminPage = () => {
  useSEO("Admin | Dr Lal PathLabs Collection Center Bhopal", "Admin enquiries dashboard.");
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);

  const fetchEnquiries = async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setEnquiries(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setEnquiries(null);
    }
  };

  useEffect(() => {
    if (token) fetchEnquiries(token);
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries(null);
  };

  if (!token) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 sm:px-6">
        <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <h1 className="font-heading text-2xl font-extrabold text-[#0B3056]">Admin Login</h1>
          <p className="mt-2 text-sm text-slate-600">Sign in to view patient enquiries.</p>
          <form onSubmit={handleLogin} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="mb-1.5 block text-sm font-medium text-slate-700">Email</label>
              <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="admin email" />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-1.5 block text-sm font-medium text-slate-700">Password</label>
              <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="password" />
            </div>
            {error && (
              <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="inline-flex min-h-[48px] w-full items-center justify-center rounded-full bg-[#1E88E5] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1565C0] disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-[#0B3056]">Patient Enquiries</h1>
          <p className="mt-1 text-sm text-slate-600">
            {enquiries ? `${enquiries.length} enquir${enquiries.length === 1 ? "y" : "ies"} received` : "Loading..."}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => fetchEnquiries(token)}
            data-testid="admin-refresh-button"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-[#F0F7FA]"
          >
            <RefreshCw size={16} aria-hidden="true" /> Refresh
          </button>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[#0B3056] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#0d3a68]"
          >
            <LogOut size={16} aria-hidden="true" /> Logout
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {enquiries && enquiries.length === 0 && (
          <div className="rounded-2xl bg-[#F0F7FA] p-10 text-center" data-testid="admin-no-enquiries">
            <p className="text-sm text-slate-600">No enquiries yet. New form submissions will appear here.</p>
          </div>
        )}
        {enquiries?.map((enq) => (
          <article key={enq.id} className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm" data-testid={`admin-enquiry-${enq.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-heading text-lg font-bold text-[#0B3056]">{enq.name}</h2>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                  <a href={`tel:+91${enq.mobile}`} className="inline-flex items-center gap-1.5 font-medium text-[#1E88E5]" data-testid={`admin-enquiry-call-${enq.id}`}>
                    <Phone size={14} aria-hidden="true" /> {enq.mobile}
                  </a>
                  {enq.email && (
                    <a href={`mailto:${enq.email}`} className="inline-flex items-center gap-1.5 font-medium text-[#1E88E5]">
                      <Mail size={14} aria-hidden="true" /> {enq.email}
                    </a>
                  )}
                </div>
              </div>
              <span className="rounded-full bg-[#F0F7FA] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#0B3056]">
                {enq.enquiry_type}
              </span>
            </div>
            <dl className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
              {enq.test && <div><dt className="text-slate-500">Test / Service</dt><dd className="font-medium text-slate-800">{enq.test}</dd></div>}
              {enq.subject && <div><dt className="text-slate-500">Subject</dt><dd className="font-medium text-slate-800">{enq.subject}</dd></div>}
              {enq.collection_preference && <div><dt className="text-slate-500">Preference</dt><dd className="font-medium text-slate-800">{enq.collection_preference}</dd></div>}
              {enq.preferred_date && <div><dt className="text-slate-500">Preferred Date</dt><dd className="font-medium text-slate-800">{enq.preferred_date}</dd></div>}
              {enq.preferred_time && <div><dt className="text-slate-500">Preferred Time</dt><dd className="font-medium text-slate-800">{enq.preferred_time}</dd></div>}
              <div><dt className="text-slate-500">Received</dt><dd className="font-medium text-slate-800">{new Date(enq.created_at).toLocaleString("en-IN")}</dd></div>
            </dl>
            {enq.message && <p className="mt-3 rounded-lg bg-[#F0F7FA] px-4 py-3 text-sm text-slate-700">{enq.message}</p>}
          </article>
        ))}
      </div>
    </div>
  );
};

export default AdminPage;
